import { useCallback, useEffect, useState } from 'react';
import { Loader, AlertCircle, NotebookPen, Pencil, Trash2, X, Check, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type { EventRetrospective, EventRetroOutcome } from '../../types/eventTeam';
import { EVENT_RETRO_OUTCOMES } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { EventOutcomeBadge } from './EventOutcomeBadge';
import { CreatorProfile } from '../CreatorProfile';
import { ConfirmationModal } from '../ConfirmationModal';

export interface EventRetrospectiveSectionProps {
  eventId: string;
  /** Prefer the retro delivered with the event detail; the section refetches on demand. */
  initialRetrospective?: EventRetrospective | null;
  perms: EventTeamPerms;
  refreshNonce?: number;
  onChanged?: () => void;
}

function outcomeLabel(o: EventRetroOutcome): string {
  return o.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Retrospective (guide §1.10): outcome / attendance / what worked / what didn't /
 * lessons. Upsert by leads; delete by admins. Powers the past-events archive.
 */
export const EventRetrospectiveSection = ({
  eventId,
  initialRetrospective,
  perms,
  refreshNonce = 0,
  onChanged,
}: EventRetrospectiveSectionProps) => {
  const [retro, setRetro] = useState<EventRetrospective | null>(initialRetrospective ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [outcome, setOutcome] = useState<EventRetroOutcome | ''>('');
  const [attendance, setAttendance] = useState('');
  const [whatWorked, setWhatWorked] = useState('');
  const [whatDidnt, setWhatDidnt] = useState('');
  const [lessons, setLessons] = useState('');
  const [saving, setSaving] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventTeamApiService.getRetrospective(eventId);
      setRetro(data && (data as { id?: string }).id ? data : null);
    } catch (err) {
      // 404 → no retro yet (fine)
      const msg = err instanceof Error ? err.message : '';
      if (!msg.toLowerCase().includes('not found')) {
        setError(msg || 'Failed to load retrospective');
      }
      setRetro(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    setRetro(initialRetrospective ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const startEdit = () => {
    setOutcome(retro?.outcome ?? '');
    setAttendance(retro?.attendance_count != null ? String(retro.attendance_count) : '');
    setWhatWorked(retro?.what_worked ?? '');
    setWhatDidnt(retro?.what_didnt ?? '');
    setLessons(retro?.lessons ?? '');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await eventTeamApiService.upsertRetrospective(eventId, {
        outcome: outcome || null,
        attendance_count: attendance.trim() !== '' ? Number(attendance) : null,
        what_worked: whatWorked.trim() || null,
        what_didnt: whatDidnt.trim() || null,
        lessons: lessons.trim() || null,
      });
      setRetro(saved);
      setEditing(false);
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save retrospective');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await eventTeamApiService.deleteRetrospective(eventId);
      setRetro(null);
      setShowDelete(false);
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete retrospective');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="glass-card p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="text-sm font-semibold text-glass-300 inline-flex items-center gap-2">
          <NotebookPen className="w-4 h-4 text-emerald-400" />
          Retrospective
          {loading && <Loader className="w-3.5 h-3.5 animate-spin text-glass-400" />}
        </h3>
        <div className="flex items-center gap-1.5">
          {perms.canLead && !editing && (
            <button onClick={startEdit} className="glass-button-secondary text-xs inline-flex items-center gap-1.5">
              <Pencil className="w-3.5 h-3.5" />
              {retro ? 'Edit' : 'Write retro'}
            </button>
          )}
          {perms.canAdmin && retro && !editing && (
            <button
              onClick={() => setShowDelete(true)}
              className="glass-button-secondary text-xs text-red-300 inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20 mb-3">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span className="text-sm text-red-300">{error}</span>
        </div>
      )}

      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-glass-400 mb-1 block">Outcome</label>
              <select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as EventRetroOutcome | '')}
                className="glass-input w-full text-sm"
              >
                <option value="">Not decided</option>
                {EVENT_RETRO_OUTCOMES.map((o) => (
                  <option key={o} value={o}>
                    {outcomeLabel(o)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-glass-400 mb-1 block">Attendance count</label>
              <input
                type="number"
                min={0}
                value={attendance}
                onChange={(e) => setAttendance(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="How many players showed up?"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-glass-400 mb-1 block">What worked</label>
            <textarea
              value={whatWorked}
              onChange={(e) => setWhatWorked(e.target.value)}
              className="glass-input w-full text-sm min-h-[60px]"
            />
          </div>
          <div>
            <label className="text-xs text-glass-400 mb-1 block">What didn't</label>
            <textarea
              value={whatDidnt}
              onChange={(e) => setWhatDidnt(e.target.value)}
              className="glass-input w-full text-sm min-h-[60px]"
            />
          </div>
          <div>
            <label className="text-xs text-glass-400 mb-1 block">Lessons for next time</label>
            <textarea
              value={lessons}
              onChange={(e) => setLessons(e.target.value)}
              className="glass-input w-full text-sm min-h-[60px]"
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setEditing(false)} className="glass-button-secondary text-xs inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" />
              Cancel
            </button>
            <button
              onClick={() => void handleSave()}
              disabled={saving}
              className="glass-button text-xs inline-flex items-center gap-1"
            >
              {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Save retrospective
            </button>
          </div>
        </div>
      ) : retro ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <EventOutcomeBadge outcome={retro.outcome} size="md" />
            {retro.attendance_count != null && (
              <span className="text-sm text-glass-300 inline-flex items-center gap-1">
                <Users className="w-4 h-4 text-purple-400" />
                {retro.attendance_count} attendee{retro.attendance_count === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-lg bg-green-500/5 border border-green-500/15 p-3">
              <p className="text-[11px] text-green-300 font-semibold mb-1 uppercase tracking-wide">What worked</p>
              <p className="text-sm text-glass-200 whitespace-pre-wrap">{retro.what_worked || '—'}</p>
            </div>
            <div className="rounded-lg bg-red-500/5 border border-red-500/15 p-3">
              <p className="text-[11px] text-red-300 font-semibold mb-1 uppercase tracking-wide">What didn't</p>
              <p className="text-sm text-glass-200 whitespace-pre-wrap">{retro.what_didnt || '—'}</p>
            </div>
            <div className="rounded-lg bg-cyan-500/5 border border-cyan-500/15 p-3">
              <p className="text-[11px] text-cyan-300 font-semibold mb-1 uppercase tracking-wide">Lessons</p>
              <p className="text-sm text-glass-200 whitespace-pre-wrap">{retro.lessons || '—'}</p>
            </div>
          </div>
          <p className="text-[11px] text-glass-500 inline-flex items-center gap-1 flex-wrap">
            Written by <CreatorProfile creatorId={retro.created_by} inline />{' '}
            {formatDistanceToNow(new Date(retro.created_at), { addSuffix: true })}
            {retro.updated_at && ` · updated ${formatDistanceToNow(new Date(retro.updated_at), { addSuffix: true })}`}
          </p>
        </div>
      ) : (
        <p className="text-glass-400 text-sm py-2">No retrospective.</p>
      )}

      {showDelete && (
        <ConfirmationModal
          isOpen={showDelete}
          onClose={() => setShowDelete(false)}
          onConfirm={() => void handleDelete()}
          title="Delete Retrospective"
          message="Delete this retrospective? This cannot be undone."
          confirmText="Delete"
          isLoading={deleting}
        />
      )}
    </div>
  );
};
